import unauthenticatedApi from 'services/unauthenticatedApi';

const HANDOFF_PLAN_TITLES = {
  'wla-sprint': 'wla diagnostic sprint',
  'workforce-intelligence': 'continuous workforce intelligence',
};

export const SAMPLE_PLANS = [
  {
    id: 'sample-wla-diagnostic-sprint',
    code: 'wla-sprint',
    title: 'WLA Diagnostic Sprint (3 Months)',
    shortLabel: 'Instant Workload Audit',
    description: 'Ideal for an instant workload audit before budgeting season or during divisional reorganization.',
    badgeText: 'Best for an instant WLA audit',
    featured: true,
    ctaLabel: 'Start WLA Diagnostic Sprint',
    sortOrder: 10,
    minimumCapacity: 100,
    maximumCapacity: 1000,
    pricePerUnit: 150000,
    currency: 'IDR',
    billingCycle: 'MONTHLY',
    temporaryAccessDays: 14,
    durationMonths: 3,
    availableDurations: [3],
    sample: true,
    pricing: {
      id: 'sample-wla-diagnostic-sprint-pricing',
      pricingLabel: 'Final price shown on your invoice',
      description: 'Min. 100 license units & 3-month term. Invoice sent after order.',
    },
    features: [
      { id: 'sample-sprint-rate', description: 'Rate: IDR 150,000 / unit / month', sortOrder: 10 },
      { id: 'sample-minimum-capacity', description: 'Minimum 100 license units per project', sortOrder: 20 },
      { id: 'sample-position-count', description: 'Each position counts its usernames, minimum 1 unit', sortOrder: 30 },
      { id: 'sample-project-count', description: 'Counted per project', sortOrder: 40 },
      { id: 'sample-duration', description: '3-month initial diagnostic sprint term', sortOrder: 50 },
      { id: 'sample-invoice-access', description: 'Start with 14-day access while your invoice is pending', sortOrder: 60 },
    ],
  },
  {
    id: 'sample-continuous-workforce-intelligence',
    code: 'workforce-intelligence',
    title: 'Continuous Workforce Intelligence (Annual)',
    shortLabel: 'Continuous Capacity Intelligence',
    description: 'Quarterly dynamic workload monitoring so your headcount plan stays in sync with shifting priorities.',
    featured: false,
    ctaLabel: 'Start Continuous Intelligence',
    sortOrder: 20,
    minimumCapacity: 100,
    maximumCapacity: 1000,
    pricePerUnit: 150000,
    currency: 'IDR',
    billingCycle: 'ANNUAL',
    temporaryAccessDays: 14,
    durationMonths: 12,
    availableDurations: [12],
    sample: true,
    pricing: {
      id: 'sample-continuous-workforce-intelligence-pricing',
      pricingLabel: 'Final price shown on your invoice',
      description: 'Billed annually. Min. 100 license units. Invoice sent after order.',
      badgeText: 'Annual plan',
    },
    features: [
      { id: 'sample-annual-rate', description: 'Rate: IDR 150,000 / unit / month (annual billing)', sortOrder: 10 },
      { id: 'sample-quarterly-review', description: 'Quarterly dynamic WLA reviews throughout the year', sortOrder: 20 },
      { id: 'sample-annual-capacity', description: 'Minimum 100 license units per project', sortOrder: 30 },
      { id: 'sample-annual-position-count', description: 'Each position counts its usernames, minimum 1 unit', sortOrder: 40 },
      { id: 'sample-capacity-tracking', description: 'Project-based capacity tracking', sortOrder: 50 },
      { id: 'sample-live-baseline', description: 'Live baseline ready for dynamic Manpower Planning (MPP)', sortOrder: 60 },
      { id: 'sample-annual-access', description: 'Start with 14-day access while your invoice is pending', sortOrder: 70 },
    ],
  },
  {
    id: 'sample-enterprise-architecture',
    code: 'enterprise',
    title: 'End-to-End Enterprise Architecture',
    shortLabel: 'Large-Scale Transformation',
    description: 'Full WLA + MPP + SWP integration for complex, enterprise-grade organizational transformation.',
    featured: false,
    purchasable: false,
    ctaLabel: 'Contact Sales',
    sortOrder: 30,
    sample: true,
    pricing: {
      id: 'sample-enterprise-architecture-pricing',
      pricingLabel: 'Custom Scope / Contact Sales',
      description: 'Custom scope & enterprise SLA confirmed with WorkPlansIQ.com',
    },
    features: [
      { id: 'sample-enterprise-volume', description: 'Tailored volume pricing for large-scale organizations (>1,000 users)', sortOrder: 10 },
      { id: 'sample-enterprise-wla', description: 'Full-spectrum WLA diagnostic coverage', sortOrder: 20 },
      { id: 'sample-enterprise-mpp', description: 'Strategy-aligned Manpower Planning (MPP) simulation', sortOrder: 30 },
      { id: 'sample-enterprise-swp', description: 'SWP competency gap & talent risk analysis', sortOrder: 40 },
      { id: 'sample-enterprise-architecture-feature', description: 'Multi-entity & multi-organization enterprise architecture', sortOrder: 50 },
      { id: 'sample-enterprise-governance', description: 'Custom executive governance model & dashboard', sortOrder: 60 },
      { id: 'sample-enterprise-hris', description: 'Enterprise HRIS integration & security assessment', sortOrder: 70 },
      { id: 'sample-enterprise-commercial', description: 'Commercial details confirmed prior to onboarding', sortOrder: 80 },
    ],
  },
];

const isPlan = (plan) =>
  plan &&
  typeof plan === 'object' &&
  typeof plan.id === 'string' &&
  typeof plan.code === 'string' &&
  typeof plan.title === 'string';

export async function listActivePlans() {
  try {
    const response = await unauthenticatedApi.get('/api/v1/plans');
    const body = Array.isArray(response.data)
      ? response.data
      : response.data?.data;

    if (!Array.isArray(body)) throw new Error('Invalid plans response.');

    const plans = body
      .filter(isPlan)
      .sort((left, right) => (left.sortOrder || 0) - (right.sortOrder || 0));

    return plans.length ? plans : SAMPLE_PLANS;
  } catch {
    return SAMPLE_PLANS;
  }
}

export function findHandoffPlan(plans, planCode) {
  const directMatch = plans.find((plan) => plan.code === planCode);
  if (directMatch) return directMatch;

  const expectedTitle = HANDOFF_PLAN_TITLES[planCode];
  if (!expectedTitle) return null;

  return plans.find((plan) =>
    plan.title.toLowerCase().includes(expectedTitle),
  ) || null;
}
